import { useState, useCallback } from "react";
import {
  Box, Typography, Select, MenuItem, FormControl,
  InputLabel, Button, Divider, CircularProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ToolDialog from "../ToolDialog";

/**
 * ExportKmlTool
 * ToolBox-compatible: { title, map, layers, onClose }
 *
 * layers: { layer_id: { name, id, vectorLayer } }
 * Calls GET /export_kml?layer_id=<id> and triggers file download.
 */
export default function ExportKmlTool({ title, map, layers, onClose }) {
  const [selectedLayerId, setSelectedLayerId] = useState("");
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);

  const layerEntries = Object.entries(layers ?? {});

  const handleExport = useCallback(async () => {
    if (!selectedLayerId) return;

    const layerId =  selectedLayerId;
    const geometry_type = layers[selectedLayerId]?.geometry_types?.[0].toUpperCase();
    
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`https://app.mapgeoid.com/api-service/feature_service/export_kml?layer_id=${layerId}&geometry_type=${geometry_type}`);
      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const blob     = await res.blob();
      const url      = URL.createObjectURL(blob);
      const a        = document.createElement("a");
      a.href         = url;
      a.download     = `layer_${layerId}.kml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedLayerId, layers]);

  return (
    <ToolDialog title={title} open={true} onClose={onClose}>
      <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 2 }}>

        <FormControl fullWidth size="small">
          <InputLabel>Select Layer</InputLabel>
          <Select
            label="Select Layer"
            value={selectedLayerId}
            onChange={e => { setSelectedLayerId(e.target.value); setError(null); }}
            MenuProps={{ style: { zIndex: 2000 } }}
            displayEmpty
          >
            {layerEntries.map(([lid, linfo]) => (
              <MenuItem key={lid} value={lid}>{linfo.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {error && (
          <Typography variant="caption" color="error">{error}</Typography>
        )}

        <Divider />

        <Button
          variant="contained"
          size="small"
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
          onClick={handleExport}
          disabled={!selectedLayerId || loading}
          sx={{ textTransform: "none" }}
        >
          {loading ? "Exporting…" : "Export KML"}
        </Button>

      </Box>
    </ToolDialog>
  );
}
